import { ThemedImg } from '@/components/landingpage/themed-img';
import type { SharedData } from '@/types';
import { useAsset } from '@/utils/asset';
import { usePage } from '@inertiajs/react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface TrustBadgesCarouselProps {
    showPartner?: boolean;
    delay?: number;
    mobileOnly?: boolean;
}

export function TrustBadgesCarousel({ showPartner = false, delay = 3000, mobileOnly = false }: TrustBadgesCarouselProps) {
    const { t } = useTranslation();
    const asset = useAsset();
    const { appearance, colorScheme } = usePage<SharedData>().props;
    const scrollerRef = useRef<HTMLDivElement>(null);

    const trustLogos = [
        {
            key: 'google',
            alt: t('home.secure.logos.googleReviews'),
            src: asset('images/badges/googleBewertungen.png'),
            darkSrc: asset('images/badges/googleBewertungen-dark.png'),
            className: 'h-24 w-auto object-contain',
        },
        {
            key: 'konform',
            alt: t('home.secure.logos.konform'),
            src: asset('images/badges/konform.png'),
            darkSrc: asset('images/badges/konform-dark.png'),
            className: 'h-24 w-auto object-contain',
        },
        {
            key: 'dsgvo',
            alt: t('home.secure.logos.dsgvo'),
            src: asset('images/badges/dsgvo.png'),
            className: 'h-24 w-auto object-contain dark:grayscale dark:brightness-110 dark:contrast-90',
        },
    ];
    const itemCount = trustLogos.length + (showPartner ? 1 : 0);

    useEffect(() => {
        const scroller = scrollerRef.current;

        if (!scroller || itemCount < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        let timer: number | undefined;
        const stop = () => {
            if (timer !== undefined) {
                window.clearInterval(timer);
                timer = undefined;
            }
        };
        const start = () => {
            stop();
            timer = window.setInterval(() => {
                const items = Array.from(scroller.children) as HTMLElement[];
                const viewportCenter = scroller.scrollLeft + scroller.clientWidth / 2;
                const currentIndex = items.reduce(
                    (closest, item, index) => {
                        const itemCenter = item.offsetLeft + item.clientWidth / 2;
                        const distance = Math.abs(itemCenter - viewportCenter);

                        return distance < closest.distance ? { index, distance } : closest;
                    },
                    { index: 0, distance: Number.POSITIVE_INFINITY },
                ).index;
                const next = items[(currentIndex + 1) % items.length];

                if (next) {
                    scroller.scrollTo({
                        left: next.offsetLeft - (scroller.clientWidth - next.clientWidth) / 2,
                        behavior: 'smooth',
                    });
                }
            }, delay);
        };

        if (!('IntersectionObserver' in window)) {
            start();
            return stop;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry?.isIntersecting) {
                start();
            } else {
                stop();
            }
        });
        observer.observe(scroller);

        return () => {
            stop();
            observer.disconnect();
        };
    }, [delay, itemCount]);

    return (
        <div className={mobileOnly ? 'lg:hidden' : undefined}>
            <div
                ref={scrollerRef}
                className="landing-snap-scroller flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain"
                role="region"
                aria-roledescription="carousel"
                aria-label={t('home.secure.title')}
            >
                {trustLogos.map((logo) => (
                    <div key={logo.key} className="flex w-[80%] shrink-0 snap-center items-center justify-center sm:w-[60%]">
                        <ThemedImg
                            alt={logo.alt}
                            src={logo.src}
                            darkSrc={logo.darkSrc}
                            appearance={appearance}
                            initialDark={colorScheme === 'dark'}
                            className={logo.className}
                            loading="lazy"
                            decoding="async"
                            height={96}
                        />
                    </div>
                ))}
                {showPartner ? (
                    <div className="flex w-[80%] shrink-0 snap-center flex-col items-center justify-center gap-2 sm:w-[60%]">
                        <span className="text-sm font-semibold text-paragraph">{t('home.hero.partner')}</span>
                        <ThemedImg
                            src={asset('logos/VVRBLogo.png')}
                            darkSrc={asset('logos/VVRBLogoWeiss.png')}
                            appearance={appearance}
                            initialDark={colorScheme === 'dark'}
                            alt="Vereinigte Volksbank Raiffeisenbank eG"
                            className="h-20 w-auto object-contain opacity-90"
                            loading="lazy"
                            decoding="async"
                            height={80}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}
